import React, { useRef, useState, useCallback } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import "react-toastify/dist/ReactToastify.css";
import { Tooltip } from "./Tooltip";
import T from "./Translate/T";
import XIcon from "./icons/XIcon";

// 5 Concorde Place, North York, ON M3C 3M8 coordinates
const MAP_LATITUDE = 43.7276;
const MAP_LONGITUDE = -79.3367;

// API endpoint for contact form
const CONTACT_API_URL = "https://api.ramirez-carlos.com/contact";

const Contact = () => {
  const form = useRef();
  const [sendingMail, setSendingMail] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    website_url: "", // Honeypot field
  });

  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const sendEmail = useCallback(async (e) => {
    e.preventDefault();
    
    if (!executeRecaptcha) {
      toast.error("reCAPTCHA not loaded. Please refresh the page.");
      return;
    }

    setSendingMail(true);

    try {
      // Execute reCAPTCHA v3
      const recaptchaToken = await executeRecaptcha("contact_form");

      // Send to our secure API
      const response = await fetch(CONTACT_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message,
          website_url: formData.website_url, // Honeypot
          recaptcha_token: recaptchaToken,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Reset form
        setFormData({ name: "", email: "", message: "", website_url: "" });
        form.current.reset();
        
        toast.success(data.message || "Message sent successfully!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
        });
      } else {
        throw new Error(data.error || "Failed to send message");
      }
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error(error.message || "Something went wrong!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
      });
    } finally {
      setSendingMail(false);
    }
  }, [executeRecaptcha, formData]);

  return (
    <section id="contact" className="section bg-primary">
      <div className="container">
        <div className="row">
          <div className="col-lg-5 text-center text-lg-start wow fadeInUp">
            <h2 className="text-10 fw-600 mb-5">
              <T>Let's get in touch</T>
            </h2>
            <p className="text-5 mb-4">
              <T>I enjoy discussing new projects and design challenges.</T>{" "}
              <T>Please share as much info as possible so we can get the most out of our first catch-up.</T>
            </p>

            {/* Map Section */}
            <div className="mb-4" style={{ borderRadius: "8px", overflow: "hidden" }}>
              <iframe
                title="Location Map"
                src={`https://maps.google.com/maps?q=${MAP_LATITUDE},${MAP_LONGITUDE}&t=m&z=15&output=embed&iwloc=near`}
                style={{
                  width: "100%",
                  height: "200px",
                  border: "0",
                  borderRadius: "8px",
                }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            <h3 className="text-5 fw-600"><T>Living In:</T></h3>
            <address className="text-4">
              Toronto, Ontario, Canada.
            </address> 
            <h3 className="text-5 fw-600"><T>Call:</T></h3>
            <p className="text-4">(+1) 647 771 158*</p>
            <ul className="social-icons social-icons-lg justify-content-center justify-content-lg-start mt-5">
              <li className="social-icons-twitter">
                <Tooltip text="X (Twitter)" placement="top">
                  <a
                    href="https://x.com/eramirezcarlos/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <XIcon size={20} />
                  </a>
                </Tooltip>
              </li>
              <li className="social-icons-linkedin">
                <Tooltip text="LinkedIn" placement="top">
                  <a
                    href="https://www.linkedin.com/in/eramirezcarlos/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="fab fa-linkedin" />
                  </a>
                </Tooltip>
              </li>
              <li className="social-icons-github">
                <Tooltip text="Github" placement="top">
                  <a
                    href="https://github.com/eramirezcarlos"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="fab fa-github" />
                  </a>
                </Tooltip>
              </li>
            </ul>
          </div>
          <div
            className="col-lg-6 ms-auto mt-5 mt-lg-0 wow fadeInUp"
            data-wow-delay="0.3s"
          >
            <h2 className="text-10 fw-600 text-center text-lg-start mb-5">
              <T>Estimate your Project?</T>
            </h2>
            {/* Contact Form */}
            <form
              id="contact-form"
              className="form-border"
              method="post"
              ref={form}
              onSubmit={sendEmail}
            >
              <div className="row g-4">
                <div className="col-12">
                  <label className="form-label" htmlFor="name">
                    <T>What is Your Name:</T>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    className="form-control py-1"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    maxLength={100}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label" htmlFor="email">
                    <T>Your Email Address:</T>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    className="form-control py-1"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    maxLength={100}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label" htmlFor="message">
                    <T>How can I Help you?:</T>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    className="form-control py-1"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={2000}
                  />
                </div>
                
                {/* Honeypot field - hidden from users, bots will fill it */}
                <div style={{ position: "absolute", left: "-9999px" }} aria-hidden="true">
                  <label htmlFor="website_url">Website URL</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website_url}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-12 text-center text-lg-start">
                  <button
                    id="submit-btn"
                    className="btn btn-dark rounded-0"
                    type="submit"
                    disabled={sendingMail}
                  >
                    {sendingMail ? (
                      <>
                        <span
                          role="status"
                          aria-hidden="true"
                          className="spinner-border spinner-border-sm align-self-center me-2"
                        ></span>
                        <T>Sending...</T>
                      </>
                    ) : (
                      <>
                        <T>Send</T>{" "}
                        <span className="ms-3">
                          <i className="fas fa-arrow-right" />
                        </span>
                      </>
                    )}
                  </button>
                </div>
                <div className="col-12">
                  <p className="text-muted small mb-0">
                    <T>This site is protected by reCAPTCHA and the Google Privacy Policy and Terms of Service apply.</T>
                  </p>
                </div>
                <ToastContainer />
              </div>
            </form>
            {/* Contact Form end */}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;